<template>
  <div
    class="booking-engine__autocomplete active"
    :class="{ 'is-current': is_current }"
  >
    <div class="vb-form">
      <autocomplete
        ref="autocomplete"
        base-class="booking-engine__autocomplete"
        :search="get_keyds"
        :placeholder="autocomplete_placeholder"
        :debounce-time="500"
        :get-result-value="
          (r) => (r && r.type !== 'noresults' && r.label) || ''
        "
        :submit-on-enter="true"
        @update="on_autocomplete_update"
        @submit="on_submit_location_internal"
      >
        <template
          #default="{
            inputProps,
            inputListeners,
            results,
            resultProps,
            resultListProps,
            resultListListeners,
          }"
        >
          <div class="booking-engine__autocomplete__form">
            <div class="booking-engine__autocomplete__icon">
              <vb-icon :name="'pin'" color="--primary" :size="20" />
            </div>
            <input
              type="search"
              class=""
              :class="[
                'vb-input form-control',
                { focus: current_tab === 1 },
                { 'autocomplete-input-selected': autocomplete_selected },
              ]"
              v-bind="inputProps"
              :readonly="is_only_stay_package"
              v-on="inputListeners"
              @focus="
                ($event) => {
                  autocomplete_focus = true;
                  $emit('scroll-into-view');
                  $event.target.select();
                  $emit('update:current_tab', 1);
                }
              "
              @blur="
                () => {
                  autocomplete_focus = false;
                  autocomplete_blur();
                }
              "
            />
          </div>

          <b-row>
            <b-col>
              <ul
                v-bind="resultListProps"
                class="booking-engine__autocomplete-result-wrap"
                v-on="resultListListeners"
              >
                <li
                  v-for="(result, index) in results"
                  :key="resultProps[index].keyd"
                  :style="
                    result.type == 'noresults' ? 'pointer-events: none' : ''
                  "
                  :data-type="result.type"
                  v-bind="resultProps[index]"
                >
                  <div
                    v-if="result.type === 'noresults'"
                    v-html="result.label"
                  ></div>
                  <div
                    v-else
                    class="cursor-pointer"
                    :style="{
                      'padding-left': `calc(${result.level - 1} *0.5rem)`,
                    }"
                    v-html="
                      `${result.label.charAt(0).toUpperCase()}${result.label.slice(1).toLowerCase()}`
                    "
                  ></div>
                </li>
              </ul>
            </b-col>
          </b-row>

          <div
            v-show="!resultProps.length"
            class="booking-engine__floating--suggestions"
            @mousedown.stop
          >
            <keyd-map
              v-if="keyd_map_init"
              ref="keyd-map"
              @keyd="on_submit_location_internal"
            />
          </div>
        </template>
      </autocomplete>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import KeydMap from '@alpitour/components/blocks/keyd-map/keyd-map.vue';
  import { get_keyds, get_autocomplete_placeholder } from './logic';

  export default {
    name: 'booking-engine-destination',

    components: {
      'keyd-map': KeydMap,
    },

    props: {
      current_tab: {
        type: Number,
        required: true,
      },
      location: {
        type: Object,
        required: true,
      },
      keyd_map_init: {
        type: Boolean,
        required: true,
      },
      keydsmap: {
        type: Array,
        required: true,
      },
      is_current: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        autocomplete_focus: false,
      };
    },

    computed: {
      ...mapGetters('liveat', ['is_only_stay_package']),
      autocomplete_placeholder() {
        return get_autocomplete_placeholder(
          this.autocomplete_focus,
          this.keydsmap,
          this.location
        );
      },
      autocomplete_selected() {
        return (
          !this.autocomplete_focus &&
          this.keydsmap.length > 0 &&
          !!this.location.label
        );
      },
    },

    methods: {
      get_keyds,
      autocomplete_blur() {
        this.$refs['autocomplete'].$data.value = ''; //this.location.label
      },
      on_submit_location_internal(location) {
        this.autocomplete_focus = false;
        this.autocomplete_blur();
        // es. voce "nessun risultato": non è una destinazione selezionabile
        if (location && location.type === 'noresults') return;
        this.$emit('submit-location', location);
      },
      // La libreria autocomplete scrolla l'elemento evidenziato solo tramite i
      // suoi ref interni (input/resultList), assenti quando si usa lo slot custom.
      // Replichiamo lo scroll sul contenitore ad ogni cambio di selectedIndex.
      on_autocomplete_update(results, selectedIndex) {
        if (selectedIndex < 0) return;
        this.$nextTick(() => {
          const wrap = this.$el.querySelector(
            '.booking-engine__autocomplete-result-wrap'
          );
          if (!wrap) return;
          const item = wrap.querySelector(
            `[data-result-index="${selectedIndex}"]`
          );
          if (!item) return;
          const wrap_rect = wrap.getBoundingClientRect();
          const item_rect = item.getBoundingClientRect();
          if (item_rect.top < wrap_rect.top) {
            wrap.scrollTop -= wrap_rect.top - item_rect.top;
          } else if (item_rect.bottom > wrap_rect.bottom) {
            wrap.scrollTop += item_rect.bottom - wrap_rect.bottom;
          }
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  %step {
    z-index: 10;
    height: 100%;

    &:not(.active) {
      pointer-events: none;

      input {
        border-color: $gray-300;

        &::placeholder {
          color: $gray-500;
        }
      }
    }
  }

  %bg-window {
    padding: 1rem;
    border-radius: 0.313rem;
    margin-top: 0.65rem;
    background-color: $white;
  }

  .booking-engine {
    $this: &;

    &__floating {
      display: none;
      @extend %bg-window;

      @include media-breakpoint-up(md) {
        position: absolute;
        top: auto;
        left: -$grid-gutter-width/4;
        right: -$grid-gutter-width/4;
        width: auto;
      }

      @include media-breakpoint-up(xl) {
        left: calc(100% / 12 - #{$grid-gutter-width/6});
        right: calc(100% / 12 - #{$grid-gutter-width/6});
      }

      &--suggestions {
        @extend .booking-engine__floating;
      }
    }

    // Destinazione
    &__autocomplete {
      @extend %step;
      position: static !important; // override @trevoreyre/autocomplete-vue inline style
      z-index: 11;

      &.is-current {
        .booking-engine__floating {
          display: block;
        }
      }

      &__form {
        position: relative;

        input {
          padding-left: $grid-gutter-20 * 2;

          &:placeholder-shown {
            font-style: italic;
          }

          &.autocomplete-input-selected:placeholder-shown {
            font-style: normal;
            font-weight: 600;
          }
        }
      }

      &__icon {
        position: absolute;
        top: 50%;
        left: $grid-gutter-20 / 2;

        transform: translateY(-50%);
        aspect-ratio: 1 / 1;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      input {
        caret-color: $info;

        &[readonly] {
          background-color: $white;
          cursor: pointer;
        }

        &:focus {
          & ~ .booking-engine__autocomplete__icon {
            display: none;
          }
        }
      }

      // classi generate con vue-autocomplete
      &-result {
        list-style: none;
        border-bottom: 1px solid $gray-300;
        margin-bottom: 0.313rem;
        padding-bottom: 0.25rem;
        text-transform: capitalize;
        background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiA/Pjxzdmcgdmlld0JveD0iMCAwIDMyIDMyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxwYXRoIGQ9Ik0gMTYgMyBDIDExLjA0Mjk2OSAzIDcgNy4wNDI5NjkgNyAxMiBDIDcgMTMuNDA2MjUgNy41NzAzMTMgMTUuMDE5NTMxIDguMzQzNzUgMTYuNzgxMjUgQyA5LjExNzE4OCAxOC41NDI5NjkgMTAuMTEzMjgxIDIwLjQxNDA2MyAxMS4xMjUgMjIuMTU2MjUgQyAxMy4xNDg0MzggMjUuNjQ0NTMxIDE1LjE4NzUgMjguNTYyNSAxNS4xODc1IDI4LjU2MjUgTCAxNiAyOS43NSBMIDE2LjgxMjUgMjguNTYyNSBDIDE2LjgxMjUgMjguNTYyNSAxOC44NTE1NjMgMjUuNjQ0NTMxIDIwLjg3NSAyMi4xNTYyNSBDIDIxLjg4NjcxOSAyMC40MTQwNjMgMjIuODgyODEzIDE4LjU0Mjk2OSAyMy42NTYyNSAxNi43ODEyNSBDIDI0LjQyOTY4OCAxNS4wMTk1MzEgMjUgMTMuNDA2MjUgMjUgMTIgQyAyNSA3LjA0Mjk2OSAyMC45NTcwMzEgMyAxNiAzIFogTSAxNiA1IEMgMTkuODc4OTA2IDUgMjMgOC4xMjEwOTQgMjMgMTIgQyAyMyAxMi44MDA3ODEgMjIuNTcwMzEzIDE0LjMxNjQwNiAyMS44NDM3NSAxNS45Njg3NSBDIDIxLjExNzE4OCAxNy42MjEwOTQgMjAuMTEzMjgxIDE5LjQ1MzEyNSAxOS4xMjUgMjEuMTU2MjUgQyAxNy41NTQ2ODggMjMuODY3MTg4IDE2LjU3ODEyNSAyNS4zMDA3ODEgMTYgMjYuMTU2MjUgQyAxNS40MjE4NzUgMjUuMzAwNzgxIDE0LjQ0NTMxMyAyMy44NjcxODggMTIuODc1IDIxLjE1NjI1IEMgMTEuODg2NzE5IDE5LjQ1MzEyNSAxMC44ODI4MTMgMTcuNjIxMDk0IDEwLjE1NjI1IDE1Ljk2ODc1IEMgOS40Mjk2ODggMTQuMzE2NDA2IDkgMTIuODAwNzgxIDkgMTIgQyA5IDguMTIxMDk0IDEyLjEyMTA5NCA1IDE2IDUgWiBNIDE2IDEwIEMgMTQuODk0NTMxIDEwIDE0IDEwLjg5NDUzMSAxNCAxMiBDIDE0IDEzLjEwNTQ2OSAxNC44OTQ1MzEgMTQgMTYgMTQgQyAxNy4xMDU0NjkgMTQgMTggMTMuMTA1NDY5IDE4IDEyIEMgMTggMTAuODk0NTMxIDE3LjEwNTQ2OSAxMCAxNiAxMCBaIi8+PC9zdmc+');
        background-repeat: no-repeat;
        background-position: left center;
        background-size: 1.2rem;
        padding-left: 1.563rem;

        &[aria-selected='true'] {
          position: relative;
          z-index: 1;
          box-shadow: inset 0 0 0 2px var(--primary);
          border-radius: $btn-border-radius;
          // evita che il separatore si sovrapponga al bordo
          border-bottom-color: transparent;
        }

        &:hover {
          color: $info;
        }

        &:last-child {
          border-bottom: 0;
          margin-bottom: 0;
          padding-bottom: 0;
        }

        // wrap li, contenitore per la scrollbar
        &-wrap {
          overflow: auto;
          max-height: 250px;

          // solo per firefox
          scrollbar-width: thin;
          scrollbar-color: $gray-500 transparent;
          //

          &::-webkit-scrollbar {
            width: 4px;
          }

          &::-webkit-scrollbar-track {
            box-shadow: none;
          }

          &::-webkit-scrollbar-thumb {
            background-color: $gray-600;
            border-radius: 2.5px;
          }
        }

        // ul
        &-list {
          @extend %bg-window;
        }
      }

      &__floating--suggestions {
        @include media-breakpoint-up(md) {
          left: calc(100% / 12);
          width: auto;
        }
      }
    }
  }
</style>
